import {useRef, useState} from "react";
import styled from "styled-components";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import type {UIVersion} from "../../../data/state/AppState";
import {TimeUtils} from "../../../utils/TimeUtils";
import {useAppStore} from "../../../StateManager";
import CircleQuestionIcon from "../icons/circle-question.svg?react";
import KeyboardIcon from "../icons/keyboard.svg?react";
import NotebookIcon from "../icons/notebook.svg?react";
import LightbulbIcon from "../icons/lightbulb-alt.svg?react";
import BugIcon from "../icons/bug.svg?react";
import GearIcon from "../icons/gear.svg?react";
import MessageSquareExclamationIcon from "../icons/message-square-exclamation.svg?react";
import ArrowRightFromBracketIcon from "../icons/arrow-right-from-bracket.svg?react";
import V5Icon from "../icons/v5-icon.svg?react";
import type {IDropdownElement} from "../dropdown/TopBarDropdownV5";
import {TopBarDropdownV5} from "../dropdown/TopBarDropdownV5";
import {ShortcutWindowV5} from "../popup/ShortcutWindowV5";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import type {UpdateUserProfileRequest} from "../../../generated/api/base";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {DateUtils} from "../../../utils/DateUtils";
import {NavigationEnum, SettingsSubNav} from "../../../Enums";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {colorPalette} from "../styles/colorPalette";
import {InitialsV5, IsRoundClassName} from "../widgets/InitialsV5";
import {ToggleSwitchV5} from "../widgets/switch/ToggleSwitchV5";
import {baseDistance} from "../styles/styles";

export const UserProfileV5 = () => {
	const _avatar = useRef<HTMLDivElement>(null);

	const appState = useAppStore((state) => state.appState);
	const {setNavBarState, navBarState} = useAppStore();
	const [isShortcutOpen, setIsShortcutOpen] = useState<boolean>(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

	const onSettingsClick = () => {
		appState.app.navigation.goApp({
			mainNav: NavigationEnum.SETTINGS,
			subNavs: [SettingsSubNav.PERSONAL],
		});
		setNavBarState("hidden");
		setIsDropdownOpen(false);
	};

	const onMyProfileClick = () => {
		appState.app.navigation.goApp({
			mainNav: NavigationEnum.SETTINGS,
			subNavs: [SettingsSubNav.PERSONAL],
		});
		if (navBarState === "docked") {
			setNavBarState("closed");
		}
		setIsDropdownOpen(false);
	};

	const onToggleLiveChatBox = () => {
		const liveChat: HTMLDivElement = document.querySelector("#hubspot-messages-iframe-container.widget-align-left");

		liveChat?.classList.toggle("open");
		setIsDropdownOpen(false);
	};

	const onShortCutClicked = async () => {
		if (!isShortcutOpen) {
			setIsShortcutOpen(true);
		} else {
			setIsShortcutOpen(false);
			await TimeUtils.waitForNextFrame();
			setIsShortcutOpen(true);
		}
		setIsDropdownOpen(false);
	};

	const onCloseShortcut = () => {
		setIsShortcutOpen(false);
		setIsDropdownOpen(false);
	};

	const onDocsClicked = () => {
		appState.app.navigation.openInNewTab("https://support.xyicon.com");
		setIsDropdownOpen(false);
	};

	const onFeatureClicked = () => {
		appState.app.navigation.openInNewTab("https://support.xyicon.com/docs/submit-a-feature-request");
		setIsDropdownOpen(false);
	};

	const onBugClicked = () => {
		appState.app.navigation.openInNewTab("https://support.xyicon.com/docs/report-an-issue");
		setIsDropdownOpen(false);
	};

	const onLogoutClick = () => {
		return appState.app.transport.services.auth.logout();
	};

	const onUITogglerClick = () => {
		setIsDropdownOpen(false);

		if (appState.selectedMenu === NavigationEnum.HOME) {
			appState.app.navigation.goApp({
				mainNav: NavigationEnum.PORTFOLIOS,
			});
		}
		const newVersion: UIVersion = "4.0";
		const date = DateUtils.stringify(new Date());
		const v5ToggleCount = user.preferences.generalSetting.v5ToggleCount ?? 0;

		appState.setUIVersion(newVersion);
		user.generalSetting.v5ToggleCount = v5ToggleCount;

		const params: UpdateUserProfileRequest = {
			firstName: user.firstName,
			lastName: user.lastName,
			username: user.username,
			phoneNumber: user.phone,
			countryCode: user.countryCode,
			userGeneralSetting: {
				timeZone: "GMT+2",
				language: "en",
				currentUiVersion: newVersion,
				lastToggleOnAt: date,
				v5ToggleCount: v5ToggleCount,
			},
		};

		return appState.app.transport.requestForOrganization<UpdateUserProfileRequest[]>({
			url: "users/updateuserprofile",
			method: XHRLoader.METHOD_POST,
			params,
		});
	};

	useClickOutside([_avatar], () => setIsDropdownOpen(false), "mouseup");

	const user = appState.user;
	const initials = user ? appState.actions.renderInitials(user.id) : "";
	const avatarBackgroundColor = colorPalette.positive.c500Primary;

	const dropdownElements: IDropdownElement[] = [
		{
			icon: (
				<InitialsV5
					className={IsRoundClassName}
					backgroundColor={avatarBackgroundColor}
					size={24}
					name={initials}
				/>
			),
			onMouseDown: onMyProfileClick,
			label: "My profile",
		},
		{
			icon: <CircleQuestionIcon />,
			label: "Help",
			subElements: [
				{
					icon: <MessageSquareExclamationIcon />,
					onMouseDown: onToggleLiveChatBox,
					label: "Live Chat",
				},
				{
					icon: <KeyboardIcon />,
					onMouseDown: onShortCutClicked,
					label: "Keyboard Shortcuts",
				},
				{
					icon: <NotebookIcon />,
					onMouseDown: onDocsClicked,
					label: "Help Documents",
				},
				{
					icon: <LightbulbIcon />,
					onMouseDown: onFeatureClicked,
					label: "Suggest a Feature",
				},
				{
					icon: <BugIcon />,
					onMouseDown: onBugClicked,
					label: "Report a Problem",
				},
			],
		},
		{
			icon: <GearIcon />,
			onMouseDown: onSettingsClick,
			label: "Settings",
		},
		{
			icon: <V5Icon />,
			onMouseDown: onUITogglerClick,
			label: "Version 5 (Beta)",
			hasLineOnTop: true,
			rightContent: (
				<ToggleSwitchV5Styled
					noLabel
					value={true}
					onChange={null}
				/>
			),
		},
		{
			icon: <ArrowRightFromBracketIcon />,
			onMouseDown: onLogoutClick,
			label: "Logout",
		},
	];

	useCloseOnKeyboardPress(() => setIsDropdownOpen(false), KeyboardListener.KEY_ESCAPE);

	return (
		<>
			<InitialsV5
				size={32}
				className={IsRoundClassName}
				backgroundColor={avatarBackgroundColor}
				name={initials}
				onClick={() => setIsDropdownOpen(!isDropdownOpen)}
				divRef={_avatar}
			/>
			{isDropdownOpen && (
				<DomPortal destination={appState.app.modalContainer}>
					<TopBarDropdownV5
						elements={dropdownElements}
						parent={_avatar}
						verticalAlignment={VerticalAlignment.bottomOuter}
						horizontalAlignment={HorizontalAlignment.right}
					/>
				</DomPortal>
			)}
			{isShortcutOpen && (
				<DomPortal destination={document.body}>
					<ShortcutWindowV5 onClose={onCloseShortcut} />
				</DomPortal>
			)}
		</>
	);
};

export const ToggleSwitchV5Styled = styled(ToggleSwitchV5)`
	padding-right: ${baseDistance.sm};
`;
